import React, { useState, useEffect } from 'react';
import { LockClosedIcon, ComputerDesktopIcon, TrashIcon } from '@heroicons/react/24/outline';
import settingsService from '../../services/settingsService';

const SecurityTab = ({ onNotify }) => {
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      setLoadingSessions(true);
      const data = await settingsService.getActiveSessions();
      setSessions(data);
    } catch {
      // Endpoint may not return data if table doesn't exist yet
      setSessions([]);
    } finally {
      setLoadingSessions(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      onNotify('New passwords do not match', 'error');
      return;
    }
    if (passwordForm.new_password.length < 8) {
      onNotify('Password must be at least 8 characters', 'error');
      return;
    }
    setChangingPassword(true);
    try {
      await settingsService.changePassword({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      onNotify('Password changed successfully');
    } catch (err) {
      onNotify(err.response?.data?.detail || 'Failed to change password', 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    if (!window.confirm('Revoke this session? You will be logged out from that device.')) return;
    try {
      await settingsService.revokeSession(sessionId);
      onNotify('Session revoked');
      loadSessions();
    } catch {
      onNotify('Failed to revoke session', 'error');
    }
  };

  const handleRevokeAll = async () => {
    if (!window.confirm('Revoke ALL sessions? You will need to log in again.')) return;
    try {
      await settingsService.revokeAllSessions();
      onNotify('All sessions revoked');
      loadSessions();
    } catch {
      onNotify('Failed to revoke sessions', 'error');
    }
  };

  const passwordStrength = () => {
    const pw = passwordForm.new_password;
    if (!pw) return { label: '', color: '', width: '0%' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const levels = [
      { label: 'Very Weak', color: 'bg-red-500', width: '20%' },
      { label: 'Weak', color: 'bg-orange-500', width: '40%' },
      { label: 'Fair', color: 'bg-yellow-500', width: '60%' },
      { label: 'Good', color: 'bg-blue-500', width: '80%' },
      { label: 'Strong', color: 'bg-green-500', width: '100%' },
    ];
    return levels[Math.min(score, 4)];
  };

  const strength = passwordStrength();

  return (
    <div className="space-y-6">
      {/* Change Password */}
      <form onSubmit={handlePasswordChange} className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <LockClosedIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />
          <h3 className="text-lg font-medium text-white">Change Password</h3>
        </div>
        <div className="space-y-4">
          <div>
            <label htmlFor="current_password" className="block text-sm font-medium text-gray-300 mb-2">Current Password</label>
            <input
              id="current_password"
              name="current_password"
              type="password"
              autoComplete="current-password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              required
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
            <input
              id="new_password"
              name="new_password"
              type="password"
              autoComplete="new-password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              required
              minLength={8}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="At least 8 characters"
            />
            {strength.label && (
              <div className="mt-2">
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className={`h-full ${strength.color} transition-[width] duration-300 motion-reduce:transition-none`} style={{ width: strength.width }} />
                </div>
                <p className="text-xs text-gray-500 mt-1" aria-live="polite">Strength: {strength.label}</p>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-300 mb-2">Confirm New Password</label>
            <input
              id="confirm_password"
              name="confirm_password"
              type="password"
              autoComplete="new-password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              required
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Re-enter new password"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={changingPassword}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-6 py-2 rounded-lg transition-colors motion-reduce:transition-none"
            >
              {changingPassword ? 'Changing…' : 'Change Password'}
            </button>
          </div>
        </div>
      </form>

      {/* Active Sessions */}
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <ComputerDesktopIcon className="h-6 w-6 text-blue-400" />
            <h3 className="text-lg font-medium text-white">Active Sessions</h3>
          </div>
          {sessions.length > 0 && (
            <button
              onClick={handleRevokeAll}
              className="text-sm text-red-400 hover:text-red-300"
            >
              Revoke All
            </button>
          )}
        </div>
        {loadingSessions ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-gray-500 text-sm py-4 text-center">No active sessions found.</p>
        ) : (
          <div className="space-y-2">
            {sessions.map((session) => (
              <div
                key={session.id}
                className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3"
              >
                <div className="flex items-center gap-3">
                  <ComputerDesktopIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
                  <div>
                    <p className="text-sm text-gray-300">
                      Session #{session.id}
                      {session.is_current && (
                        <span className="ml-2 text-xs text-green-400">(Current)</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500">
                      Created: {new Date(session.created_at).toLocaleString()} ·
                      Expires: {new Date(session.expires_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRevokeSession(session.id)}
                  className="text-red-400 hover:text-red-300 p-1"
                  aria-label={`Revoke session #${session.id}`}
                  title="Revoke session"
                >
                  <TrashIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityTab;
